import fb from '../../firebase'
import * as Sentry from '@sentry/browser'
import { UserState, RootState } from '../types'
import { ActionContext } from 'vuex'
import { UserProfile } from '@/types/users'
import { Team } from '@/types/teams'
import store, { moduleActionContext } from '@/store'
import { UserNetworkStats } from '@/types/networkstats'
import { CollectionNames, DeepPartial, KeyDict } from '@/types'
import { CallTriage, Cast } from '@/types/casts'
import { firestore } from 'firebase'
import FirebasePresence from '@/classes/FirebasePresence'
import { cloneDeep } from 'lodash'
import { Feature, hasFeature } from '@/featureFlags/featureFlags'
import { AbilityRole } from '@/abilitiesByRole'

type UserContext = ActionContext<UserState, RootState>

let connectedRef: firebase.database.Reference | undefined = undefined

interface AssignParams {
  anonymousProfile: UserProfile,
  existingProfile: UserProfile,
  castId: string,
  updates: {
    streams: boolean,
    profiles: boolean,
    sessions: boolean
  }
}

const defaultUserState: UserState = {
  userLoggedIn: false,
  online: true,
  firebaseLogin: false,
  loginComplete: false,
  teamCheck: null,
  userHasNoTeams: false,
  email: '',
  id: '',
  teamId: null,
  profile: null,
  teamRole: '',
  showProfile: false,
  needRepublish: false,
  defaultPageOnLogin: null,
  teams: [],
  legalAgreements: {
    tosVersion: 0,
    privacyVersion: 0,
    lastUpdateDialog: null,
  },
  presence: null,
  isDumping: false
}

const userModule = {
  namespaced: true,
  // We use `cloneDeep()` because `defaultUserState` is also used to reset the state in `userLogout()`. And otherwise
  // `defaultUserState` would get "polluted" with the extra getters and setters Vue adds to `state` (which would in that
  // case then be the same object).
  state: cloneDeep(defaultUserState),
  getters: {
    userLoggedIn: (state: UserState) => state.userLoggedIn,
    currentUserId: (state: UserState) => state.id,
    userEmail: (state: UserState) => state.email,
    teamId: (state: UserState) => state.teamId,
    userProfile: (state: UserState) => state.profile,
    showSelfServeWelcomeMessage: (state: UserState): boolean => {
      if (!hasFeature(Feature.SELF_SERVE)) return false
      const hasShown = state.profile?.selfSelveWelcomeShown ?? false
      return !hasShown
    }
  },
  actions: {
    updateDefaultPageOnLogin (context: UserContext, page: string|null) {
      const { commit } = userModuleActionContext(context)
      commit.updateDefaultPageOnLogin(page)
    },
    async signInUser (context: UserContext, user: firebase.User) {
      const { commit } = userModuleActionContext(context)
      commit.userLoggedIn(user)
    },
    subscribeOnlineStatus (context: UserContext) {
      const { commit, dispatch } = userModuleActionContext(context)
      if (connectedRef) {
        dispatch.unsubscribeOnlineStatus()
      }
      // FIXME needs to move to the document snapper
      connectedRef = fb.firebase.database().ref(".info/connected")
      if (connectedRef) {
        connectedRef.on("value", snap => {
          const value = snap === null ? false : snap.val() ?? false
          commit.setOnline(value)
        })
      }
    },
    unsubscribeOnlineStatus (_context: UserContext) {
      if (connectedRef !== undefined) {
        connectedRef.off("value")
        connectedRef = undefined
      }
    },
    async setUserProfile (context: UserContext, profile: UserProfile) {
      const { commit, dispatch, state } = userModuleActionContext(context)
      commit.setUserProfile(profile)
      if (profile.teams) {
        // this loads in the teams the user is apart of that are not deleted
        await dispatch.loadUsersTeams(profile.teams)
        // Check that the currentTeam corresponds to a valid, non-deleted team.
        const currentTeamInProfileTeams = state.teams.some(team => team.id === profile.currentTeam)
        if (profile.currentTeam && (currentTeamInProfileTeams || profile.role === AbilityRole.SuperUser)) {
          dispatch.setUserTeam(profile.currentTeam)
          await store.dispatch.team.setActiveTeam(profile.currentTeam)
        } else {
          const teamMember = state.teams[0]
          if (teamMember !== undefined) {
            commit.toggleUserHasNoTeams(false)
            // Note: Updating the user profile with the current team will cause the snapshot listener that is set up in
            // `AuthManager::getUserProfile()` to be triggered again. Which in turn will also cause this method to be
            // called again, but then with a `currentTeam`. And thus the `setActiveTeam()` in the code path above will
            // be taken.
            await dispatch.updateProfile({ currentTeam: teamMember.id })
            return
          } else {
            if (profile.role === 'user') {
              commit.toggleUserHasNoTeams(true)
            }
          }
        }
      }
    },
    async loadUsersTeams (context: UserContext, teams: KeyDict<boolean>) {
      const { commit } = userModuleActionContext(context)
      const activeTeams: Team[] = []
      for (const key in teams) {
        const team = await fb.db.collection('teams').doc(key).get()
        const teamData = team.data() as Team
        teamData.id = team.id
        if (!teamData.deleted) {
          activeTeams.push({ ...teamData })
        }
      }
      commit.setUsersActiveTeams(activeTeams)
    },
    async needRepublish (context: UserContext, state: boolean) {
      const { commit } = userModuleActionContext(context)
      commit.setNeedRepublish(state)
    },
    setUserTeam (context: UserContext, teamId: string|null) {
      const { commit } = userModuleActionContext(context)
      commit.setUserTeam(teamId)
    },
    setUserRoleOnTeam (context: UserContext, role: string) {
      const { commit } = userModuleActionContext(context)
      commit.setUserRoleOnTeam(role)
    },
    async updateOtherProfile (_context: UserContext, profile: Partial<UserProfile>) {
      try {
        if (profile.id !== undefined) {
          const docRef = fb.db.collection('profiles').doc(profile.id)
          await docRef.set(profile, { merge: true })
        } else {
          throw(new Error('No Id in Profile'))
        }
      }
      catch (error) {
        console.error(`updateOtherProfile : ${error}`)
      }
    },
    async updateProfile (context: UserContext, profile: Partial<UserProfile>) {
      const { state, getters } = userModuleActionContext(context)
      if (!getters.userLoggedIn) {
        return
      }
      try {
        if (state.id === '') {
          throw(new Error('My id is an empty string, so cannot update profile'))
        }
        // we delete the role so we don't overwrite the given user-level role: member or superuser
        delete profile.role
        await fb.db.collection('profiles').doc(state.id).set(profile, { merge: true })
      }
      catch (error) {
        console.error(`updateProfile : ${error}`)
      }

    },
    updateStats (context: UserContext, stats) {
      const { commit } = userModuleActionContext(context)
      commit.updateUserStats(stats)
    },
    updateLegalAgreements (context: UserContext, onlyDialogTime: boolean) {
      const { state } = userModuleActionContext(context)
      if (onlyDialogTime) { //Only update dialog time. For when prompt was dismissed
        fb.db.collection('profiles').doc(state.id).update({
          "legalAgreements.lastUpdateDialog": (new Date()).getTime() / 1000 //Set to now, in unix seconds
        })
        return
      }
      const tosVersion = store.state.admin.legalinfo.tosLastPublished.seconds
      const privacyVersion = store.state.admin.legalinfo.privacyLastPublished.seconds
      fb.db.collection('profiles').doc(state.id).update({
        legalAgreements: {
          lastUpdateDialog: (new Date()).getTime() / 1000,
          tosVersion,
          privacyVersion
        }
      })
    },
    async closeSelfServeWelcomePopup (context: UserContext) {
      const { state } = userModuleActionContext(context)
      await fb.db.collection(CollectionNames.PROFILES).doc(state.id).set({
        selfSelveWelcomeShown: true
      }, { merge: true })
    },
    updateCasterDefaultVolume (_context: UserContext, { userId, newVolume } : {userId: string, newVolume: number}): void {
      // const { state } = userModuleActionContext(context)
      if (userId) fb.db.collection('profiles').doc(userId).set({ 'caster_volume': newVolume }, { merge: true })

    },
    completeLogin (context: UserContext) {
      const { commit } = userModuleActionContext(context)
      commit.completeLogin()
    },
    toggleProfileModal (context: UserContext, showProfile: boolean) {
      const { commit } = userModuleActionContext(context)
      commit.toggleProfileModal(showProfile)
    },
    cleanupAll (_context: UserContext) {
      // FIXME needs to be refactored -> all these subscriptions should go in mixins and the components use
      // it when needed
      store.original.dispatch('events/clearCurrentCast')
      store.dispatch.team.unsubscribeActiveTeam()
      store.dispatch.team.unsubscribeTeamStreams()
      store.dispatch.team.unsubscribeCurrentTeams()
      store.dispatch.team.unsubscribeTeamInvites()
      store.dispatch.events.unsubscribeUpdateOutputStreams()
      store.dispatch.events.unsubscribeUpdateSourceStreamDocs()
      store.dispatch.assets.unsubscribeAssetGroups(store.state.events.currentCast.asset_groups)
      store.dispatch.events.unsubscribeCastTeam(undefined)
      store.original.dispatch('team/unsubscribeAllTeams')
    },
    async logoutUser (context: UserContext) {
      const { commit, dispatch } = userModuleActionContext(context)
      await dispatch.cleanupAll()
      await fb.firebase.auth().signOut()
      commit.userLogout()
    },
    async resetUser (context: UserContext) {
      const { commit } = userModuleActionContext(context)
      commit.userLogout()
    },
    setPresence (context: UserContext, presence: FirebasePresence | null) {
      const { commit } = userModuleActionContext(context)
      commit.setPresence(presence)
    },
    async assignAnonymousCaster (_context: UserContext, args: AssignParams) {
      try {
        if (args.updates.profiles) {
          const profileUpdate = {
            anonymousId: args.anonymousProfile.id,
            agent: args.anonymousProfile.agent,
            devices: args.anonymousProfile.devices,
            selectedAudioDevice: args.anonymousProfile.selectedAudioDevice,
            selectedVideoDevice: args.anonymousProfile.selectedVideoDevice
          }

          // iOS does not pass 'devices', 'selectedAudioDevice' and 'selectedVideoDevice' and the firestore update fails
          // when there's an undefined value, but not when the key is not given, so we delete the entry
          for (const [key, value] of Object.entries(profileUpdate)) {
            if (value === undefined) delete profileUpdate[key]
          }

          // update existing user so we can track the last session as anonymous
          await fb.db.collection('profiles').doc(args.existingProfile.id).set(profileUpdate, { merge: true })
          // update anonymous user to trigger a refresh of the UserProfile on their session
          await fb.db.collection('profiles').doc(args.anonymousProfile.id).set({ assignedId: args.existingProfile.id }, { merge: true })
        }

        const cast = store.state.events.currentCast
        const anonymousSessionIds = cast.online_sessions[args.anonymousProfile.id] ?? []
        const anonymousSessionId = anonymousSessionIds[anonymousSessionIds.length - 1]
        let castUpdates: DeepPartial<Cast> = {}
        if (args.updates.streams) {
          Object.entries(cast.source_streams).forEach(([streamId, stream]) => {
            if (stream.user_id === args.anonymousProfile.id) {
              castUpdates = {
                source_streams: {
                  [streamId]: {
                    user_id: args.existingProfile.id,
                    session_id: anonymousSessionId
                  }
                }
              }
            }
          })
        }

        if (args.updates.sessions) {
          const existingSessionIds = cast.online_sessions[args.existingProfile.id] ?? []
          castUpdates.online_sessions = {
            [args.existingProfile.id]: existingSessionIds.concat(anonymousSessionIds)
          }
        }

        const feedname = cast.feedname ?? {}
        Object.keys(feedname).forEach((feedId) => {
          if (feedname[feedId] === args.anonymousProfile.id) feedname[feedId] = args.existingProfile.id
        })
        castUpdates.feedname = feedname
        await fb.db.collection('casts').doc(args.castId).set(castUpdates, { merge: true })
      } catch (error) {
        console.error(error)
      }
    },
    async assignTeamGroupToUsers (context: UserContext, { userIds, groupId } : { userIds: string[], groupId: string }) {
      if(groupId === '') {
        console.error("assignTeamGroupToUsers: groupId === ''")
        return
      }
      const { state } = userModuleActionContext(context)
      try {
        const teamId = state.teamId
        if (!teamId) throw new Error("No active Team ID present")
        //Bulk edit group arrays of all users on team to match group's new member list
        await fb.db.collection('profiles').where('teams.' + [teamId], '==', true).get().then(snap => {
          const userBatch = fb.db.batch()

          snap.docs.forEach(user => {
            const currentGroups = user.data().teamGroups?.[teamId]
            //Skip if no change, user is still in group
            if (currentGroups?.includes(groupId) && userIds.includes(user.id)) return
            //Remove group from user if no longer included
            if (currentGroups?.includes(groupId) && !userIds.includes(user.id)) {
              console.log('removing :"', groupId, '" from user', user.id)
              userBatch.update(user.ref, { ['teamGroups.' + state.teamId]: firestore.FieldValue.arrayRemove(groupId)})
            }
            //Add group to user if newly assigned
            else if ((!currentGroups?.includes(groupId)) && userIds.includes(user.id)) {
                userBatch.update(user.ref, { ['teamGroups.' + teamId]: currentGroups ? firestore.FieldValue.arrayUnion(groupId) : [groupId] })
            }
          })
          userBatch.commit()
        })
      } catch(error) {
        console.error(`Error updating member group assignments for users.`, error)
      }
    },
    tryReassignAnonymousCaster (_context: UserContext) {
      const castTeam = store.state.events.castTeam
      const currentCast = store.state.events.currentCast
      const anonymousProfile = store.state.user.profile
      const existingProfile = castTeam.find((profile) => profile.id && profile.id === anonymousProfile?.assignedId)
      if (!anonymousProfile || !existingProfile || !anonymousProfile.anonymous) return

      const invitedCaster = currentCast.invited_casters[anonymousProfile.id]
      if (!invitedCaster || invitedCaster.triage !== CallTriage.Assigned) return

      const updates = {
        profiles: false,
        sessions: true,
        streams: true
      }

      let count = 0
      const anonymousSessionIds = currentCast.online_sessions[anonymousProfile.id]
      const existingSessionIds = currentCast.online_sessions[existingProfile.id]
      if (!anonymousSessionIds || anonymousSessionIds.length <= 0) return
      for (const id of anonymousSessionIds) {
        if (existingSessionIds.includes(id)) count++
      }
      // no need to update sessions if the existing user's sessionIds contain all anonymous user's sessionIds
      if (count === anonymousSessionIds.length) updates.sessions = false

      // no need to update streams if there's no anonymous' stream
      const anonymousStream = Object.values(currentCast.source_streams).find((stream) => stream.user_id === anonymousProfile.id)
      if (!anonymousStream) updates.streams = false

      if (!updates.sessions && !updates.streams) return
      store.dispatch.user.assignAnonymousCaster({ anonymousProfile, existingProfile, castId: currentCast.id, updates })
    }
  },
  mutations: {
    updateDefaultPageOnLogin (state: UserState, page: string|null) {
      state.defaultPageOnLogin = page
    },
    userLoggedIn (state: UserState, data: firebase.User) {
      state.userLoggedIn = true
      state.email = data.email ? data.email : ''
      state.id = data.uid
      state.firebaseLogin = true
      //
      Sentry.configureScope(scope => {
        if (data.email && data.uid) {
          scope.setUser({ email: data.email, id: data.uid })
        } else {
          console.error('Not able to set user for sentry, email not available')
        }
      })
    },
    async setUserProfile (state: UserState, profile: UserProfile) {
      if (profile.legalAgreements !== undefined) {
        profile.legalAgreements.lastUpdateDialog = profile.legalAgreements.lastUpdateDialog ?? 0 //Assign fallback default value
        Object.assign(state.legalAgreements, profile.legalAgreements)
      } else {
        state.legalAgreements.lastUpdateDialog = 0 //Assign fallback default value
      }

      if (profile.teams !== undefined && Object.keys(profile.teams).length > 0) {
        state.teamCheck = true
      } else {
        state.teamCheck = false
      }

      state.profile = profile
    },
    setUsersActiveTeams (state: UserState, teams: Team[]) {
      state.teams = teams
    },
    setUserTeam (state: UserState, teamId: string|null) {
      state.teamId = teamId
    },

    // this SETS the status (= write)
    // = possible confusion with other update action in networkStats (= read)
    updateUserStats (state: UserState, stats: UserNetworkStats) {
      if (state.teamId !== undefined && state.id !== '') {
        fb.db.collection('networkstats')
          .doc(state.id)
          .set({ team_id: state.teamId, ...stats }, { merge: true })
          .catch(error => {
          console.error('Update User Stats error ', error)
        })
      }
    },
    setOnline (state: UserState, online: boolean) {
      state.online = online
    },
    completeLogin (state: UserState) {
      state.loginComplete = true
    },
    toggleProfileModal (state: UserState, showProfile: boolean) {
      state.showProfile = showProfile
    },
    setUserRoleOnTeam (state: UserState, role: string) {
      state.teamRole = role
    },
    toggleUserHasNoTeams (state: UserState, status: boolean) {
      state.userHasNoTeams = status
    },
    setPresence (state: UserState, presence: FirebasePresence | null) {
      if (state.presence !== null && presence === null) {
        state.presence.closeCast()
      }
      state.presence = presence
    },
    userLogout (state: UserState) {
      store.dispatch.user.cleanupAll()
      // Note: `state = defaultUserState` would update the function parameter, but not the object it references.
      Object.assign(state, cloneDeep(defaultUserState))
    },
    setNeedRepublish (state: UserState, needRepublish: boolean) {
      state.needRepublish = needRepublish
    }
  }
} as const

export default userModule
export const userModuleActionContext = context => moduleActionContext(context, userModule)
